/* eslint-env browser */
// Settings › Donkey Miles (wave 3, W2_Portal_Settings look): earning and
// wallet redemption, airtime conversion (with the operators' logos), the
// Donkey Meter raffle and NGO donations, one setting per row, and a save bar.
//
// All keys live flat on /settings; rates follow the redeemMileRate rule (ETB
// per mile); the feature flags are read as `=== true` everywhere (absent =
// OFF). Save builds and writes exactly what the old Save did: the WHOLE
// settings object through editSettings, empty optional numbers removed
// (never written as ''), new logos uploaded first (uploadProviderIcon) and
// both providers' on/off written explicitly. Only with critical edits on.
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import SimCardOutlinedIcon from '@mui/icons-material/SimCardOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import VolunteerActivismOutlinedIcon from '@mui/icons-material/VolunteerActivismOutlined';
import UploadRoundedIcon from '@mui/icons-material/UploadRounded';
import LoyaltyOutlinedIcon from '@mui/icons-material/LoyaltyOutlined';
import { api } from 'common';
import CircularLoading from '../components/CircularLoading';
import { showToast, confirmDialog, InlineMessage } from '../components/feedback';
import { tokens as c } from '../theme';
import { SectionCard, SettingRow, SaveBar, IconCircle } from '../components/modern';

// The settings saved here survive switching Settings tabs for the rest of
// the session while the store still holds the settings they started from.
let savedCache = null; // { source, data }

const KEYS = [
  'donkeyMilesPerCurrency', 'redeemMileRate', 'mileAirtimeEnabled', 'mileAirtimeRate', 'airtimeDenominations',
  'airtimeProviderIcons', 'airtimeProviderEnabled', 'mileDonateEnabled', 'meterRewardEnabled',
  'meterRewardIncludeGuests', 'meterRewardEveryTrips', 'meterRewardCardValue',
];

const formFromSettings = (s) => ({
  donkeyMilesPerCurrency: s.donkeyMilesPerCurrency ?? '',
  redeemMileRate: s.redeemMileRate ?? '',
  mileAirtimeEnabled: s.mileAirtimeEnabled === true,
  mileAirtimeRate: s.mileAirtimeRate ?? '',
  airtimeDenominations: s.airtimeDenominations ?? '',
  airtimeProviderIcons: s.airtimeProviderIcons || {},
  // Absent = enabled: turning the feature on shows both providers until
  // one is explicitly switched off.
  airtimeProviderEnabled: s.airtimeProviderEnabled || {},
  mileDonateEnabled: s.mileDonateEnabled === true,
  meterRewardEnabled: s.meterRewardEnabled === true,
  meterRewardIncludeGuests: s.meterRewardIncludeGuests === true,
  meterRewardEveryTrips: s.meterRewardEveryTrips ?? '',
  meterRewardCardValue: s.meterRewardCardValue ?? '',
});
const settingsFor = (source) => (savedCache && source && savedCache.source === source ? savedCache.data : source || null);
const same = (a, b) => {
  const prim = (v) => v === undefined || v === null || typeof v !== 'object';
  if (prim(a) && prim(b)) return String(a === undefined || a === null ? '' : a) === String(b === undefined || b === null ? '' : b);
  return JSON.stringify(a === undefined ? null : a) === JSON.stringify(b === undefined ? null : b);
};

// Wide screens (lg and up): the groups sit in two columns (earning and
// airtime, then the raffle and donations); below lg they stack as before.
// While stacked, each column keeps the line under its last row (SettingRow
// drops it on a last child); in a column it ends clean.
const PAIR_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' },
  columnGap: 4,
  alignItems: 'start',
};
const GROUP_SX = {
  minWidth: 0,
  '& > [id$="-row"]:last-child': { borderBottom: { xs: `1px solid ${c.OUTLINE}`, lg: 0 } },
};

// `pairFirst`: the title at the top of the second column lines up with the
// first title on wide screens.
function GroupTitle({ icon, hue, title, first, pairFirst }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pt: first ? 1.5 : pairFirst ? { xs: 3.5, lg: 1.5 } : 3.5, pb: 0.5 }}>
      <IconCircle icon={icon} hue={hue} size={30} />
      <Typography component="h3" sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.4, color: c.INK }}>
        {title}
      </Typography>
    </Box>
  );
}

const MileSettings = () => {
  const { t, i18n } = useTranslation();
  const { editSettings, clearSettingsViewError } = api;
  const settingsdata = useSelector((state) => state.settingsdata);
  const dispatch = useDispatch();
  const source = settingsdata.settings;

  const [base, setBase] = useState(() => (settingsFor(source) ? formFromSettings(settingsFor(source)) : null));
  const [data, setData] = useState(() => (settingsFor(source) ? formFromSettings(settingsFor(source)) : null));
  const [clicked, setClicked] = useState(false);
  const [saving, setSaving] = useState(false);
  // Freshly chosen (not yet uploaded) logo files, keyed by provider slug.
  const [iconFiles, setIconFiles] = useState({});
  const sourceRef = useRef(source);
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (source && source !== sourceRef.current) {
      sourceRef.current = source;
      const s = settingsFor(source);
      setBase(formFromSettings(s));
      setData(formFromSettings(s));
    }
  }, [source]);

  const PROVIDERS = [
    { key: 'ethiotel', label: t('ethio_telecom', { defaultValue: 'Ethio Telecom' }) },
    { key: 'safaricom', label: t('safaricom', { defaultValue: 'Safaricom' }) },
  ];

  const changed = useMemo(() => {
    if (!data || !base) return [];
    const out = KEYS.filter((k) => !same(data[k], base[k]));
    Object.keys(iconFiles).forEach((k) => {
      if (iconFiles[k]) out.push(`icon:${k}`);
    });
    return out;
  }, [data, base, iconFiles]);
  const isChanged = (key) => changed.includes(key);

  const setSwitch = (name, checked) => setData((d) => ({ ...d, [name]: checked }));
  const handleNumberChange = (name, v) => {
    if (v === '') {
      setData((d) => ({ ...d, [name]: '' }));
      return;
    }
    const n = parseFloat(v);
    setData((d) => ({ ...d, [name]: n >= 0 ? v : '' }));
  };

  const discard = async () => {
    if (!changed.length || saving) return;
    const ok = await confirmDialog({
      title:
        changed.length === 1
          ? t('settings_discard_title_one', { defaultValue: 'Discard 1 unsaved change?' })
          : t('settings_discard_title_many', { n: changed.length, defaultValue: 'Discard {{n}} unsaved changes?' }),
      body: t('settings_discard_body', { defaultValue: 'The settings go back to how they were when you last saved.' }),
      confirmLabel: t('settings_discard', { defaultValue: 'Discard' }),
      destructive: true,
    });
    if (ok && aliveRef.current) {
      setData(base);
      setIconFiles({});
    }
  };

  const handleSubmit = async () => {
    if (!data || saving) return;
    if (!settingsdata.settings?.AllowCriticalEditsAdmin) {
      showToast.warning(t('demo_mode'));
      return;
    }

    const airtimeRate = parseFloat(data.mileAirtimeRate);
    const redeemRate = parseFloat(data.redeemMileRate);
    if (data.mileAirtimeEnabled && !(airtimeRate > 0) && !(redeemRate > 0)) {
      showToast.error(t('rate_required_when_enabled', { defaultValue: 'Set a rate greater than 0 before enabling this feature' }));
      return;
    }

    const denomsText = String(data.airtimeDenominations || '').trim();
    if (denomsText) {
      const denoms = denomsText.split(',').map((s) => parseInt(s.trim(), 10));
      if (denoms.some((n) => !(n > 0))) {
        showToast.error(t('invalid_denominations', { defaultValue: 'Denominations must be a comma-separated list of positive numbers, e.g. 50,100,200' }));
        return;
      }
    }

    // editSettings saves the whole node: the full spread is mandatory.
    const updatedSettings = {
      ...settingsdata.settings,
      mileAirtimeEnabled: data.mileAirtimeEnabled === true,
      mileDonateEnabled: data.mileDonateEnabled === true,
      meterRewardEnabled: data.meterRewardEnabled === true,
      meterRewardIncludeGuests: data.meterRewardIncludeGuests === true,
    };
    // Meter reward numbers: whole numbers with server-side defaults (10 trips,
    // 50 ETB) when left empty.
    const rewardTrips = parseInt(data.meterRewardEveryTrips, 10);
    const rewardValue = parseInt(data.meterRewardCardValue, 10);
    if (rewardTrips >= 1) { updatedSettings.meterRewardEveryTrips = rewardTrips; } else { delete updatedSettings.meterRewardEveryTrips; }
    if (rewardValue >= 1) { updatedSettings.meterRewardCardValue = rewardValue; } else { delete updatedSettings.meterRewardCardValue; }
    // Empty optional numbers are deleted from the payload, never written as ''
    // (server code does parseFloat with fallbacks on absence).
    const numeric = {
      donkeyMilesPerCurrency: parseFloat(data.donkeyMilesPerCurrency),
      redeemMileRate: redeemRate,
      mileAirtimeRate: airtimeRate,
    };
    Object.keys(numeric).forEach((k) => {
      if (numeric[k] >= 0 && !isNaN(numeric[k])) {
        updatedSettings[k] = numeric[k];
      } else {
        delete updatedSettings[k];
      }
    });
    if (denomsText) {
      updatedSettings.airtimeDenominations = denomsText;
    } else {
      delete updatedSettings.airtimeDenominations;
    }

    // Upload any newly chosen provider logos first, then save their URLs
    // with the other settings in the one whole-node write.
    setSaving(true);
    try {
      const icons = { ...(data.airtimeProviderIcons || {}) };
      for (const p of PROVIDERS) {
        if (iconFiles[p.key]) {
          icons[p.key] = await api.uploadProviderIcon(p.key, iconFiles[p.key]);
        }
      }
      if (Object.keys(icons).length > 0) {
        updatedSettings.airtimeProviderIcons = icons;
      } else {
        delete updatedSettings.airtimeProviderIcons;
      }
      setIconFiles({});
      // Explicit booleans for both providers (absent = enabled).
      const providerEnabled = {};
      PROVIDERS.forEach((p) => {
        providerEnabled[p.key] = data.airtimeProviderEnabled?.[p.key] !== false;
      });
      if (data.mileAirtimeEnabled && !PROVIDERS.some((p) => providerEnabled[p.key])) {
        setSaving(false);
        showToast.error(t('need_one_provider', { defaultValue: 'Enable at least one provider while airtime conversion is on.' }));
        return;
      }
      updatedSettings.airtimeProviderEnabled = providerEnabled;
    } catch (uploadError) {
      setSaving(false);
      showToast.error(t('icon_upload_failed', { defaultValue: 'Logo upload failed: ' }) + uploadError.message);
      return;
    }

    setClicked(true);
    try {
      await dispatch(editSettings(updatedSettings));
      savedCache = { source: sourceRef.current, data: updatedSettings };
      if (aliveRef.current) {
        const next = formFromSettings(updatedSettings);
        setBase(next);
        setData(next);
        showToast.success(t('settings_updated', { defaultValue: 'Settings updated' }));
      }
    } catch (e) {
      if (aliveRef.current) showToast.error(t('update_failed', { defaultValue: 'Update failed' }));
    } finally {
      if (aliveRef.current) setSaving(false);
    }
  };

  const handleClose = () => {
    setClicked(false);
    dispatch(clearSettingsViewError());
  };

  if (!data) return <CircularLoading inline />;

  const symbol = settingsdata.settings?.symbol || 'ETB';
  // A live example, so the admin sees what a rate means before saving.
  const effectiveAirtimeRate = parseFloat(data.mileAirtimeRate) || parseFloat(data.redeemMileRate) || 0;
  const airtimeOn = data.mileAirtimeEnabled === true;
  const meterOn = data.meterRewardEnabled === true;

  const numberField = (name, opts) => (
    <TextField
      id={`miles-${name}`}
      size="small"
      type="number"
      value={data[name]}
      onChange={(e) => handleNumberChange(name, e.target.value)}
      disabled={opts && opts.disabled}
      placeholder={opts && opts.placeholder}
      inputProps={{ min: opts && opts.min !== undefined ? opts.min : 0, step: (opts && opts.step) || 0.1, inputMode: 'decimal', 'aria-describedby': `miles-${name}-hint` }}
      sx={{ width: { xs: '100%', sm: 200 }, '& input': { fontVariantNumeric: 'tabular-nums' } }}
    />
  );

  return (
    <Box dir={i18n.dir()}>
      <SectionCard
        id="miles"
        icon={<LoyaltyOutlinedIcon />}
        hue="accent"
        title={t('mile_settings_title', { defaultValue: 'Donkey Miles settings' })}
        sx={{ display: 'flex', flexDirection: 'column', pb: 0 }}
      >
        <Typography variant="body2" sx={{ color: c.TEXT_3 }}>
          {t('mile_settings_help', { defaultValue: 'All rates are in ETB per mile. Each feature stays OFF until you enable it here and Save.' })}
        </Typography>
        {settingsdata.error && settingsdata.error.flag && clicked ? (
          <InlineMessage kind="error" onClose={handleClose} sx={{ mt: 1.5 }}>
            {t('update_failed', { defaultValue: 'Update failed' })}
          </InlineMessage>
        ) : null}

        <Box sx={PAIR_SX}>
          <Box sx={GROUP_SX}>
            {/* Earning & wallet */}
            <GroupTitle first icon={<SavingsOutlinedIcon />} hue="success" title={t('mile_earning_section', { defaultValue: 'Earning & wallet redemption' })} />
            <SettingRow
              id="miles-donkeyMilesPerCurrency"
              label={t('miles_per_currency', { defaultValue: 'Miles earned per 1 ETB spent' })}
              hint={t('miles_per_currency_help', { defaultValue: 'Accrual rate applied by the server when trips complete.' })}
              changed={isChanged('donkeyMilesPerCurrency')}
              control={numberField('donkeyMilesPerCurrency')}
            />
            <SettingRow
              id="miles-redeemMileRate"
              label={t('redeem_mile_rate', { defaultValue: 'Wallet redeem rate (ETB per mile)' })}
              hint={t('redeem_mile_rate_help', { defaultValue: 'Used by the Redeem-to-Wallet flow on the Mile screen, and to value donations.' })}
              changed={isChanged('redeemMileRate')}
              control={numberField('redeemMileRate')}
            />

            {/* Airtime conversion */}
            <GroupTitle icon={<SimCardOutlinedIcon />} hue="info" title={t('mile_airtime_section', { defaultValue: 'Airtime conversion' })} />
            <SettingRow
              id="miles-mileAirtimeEnabled"
              layout="switch"
              label={t('mile_airtime_sub', { defaultValue: 'Customers convert miles into prepaid airtime cards, delivered by SMS' })}
              changed={isChanged('mileAirtimeEnabled')}
              control={<Switch id="miles-mileAirtimeEnabled" checked={airtimeOn} onChange={(e) => setSwitch('mileAirtimeEnabled', e.target.checked)} sx={{ my: -0.75 }} />}
            />
            <SettingRow
              id="miles-mileAirtimeRate"
              label={t('airtime_mile_rate', { defaultValue: 'Airtime rate (ETB per mile)' })}
              hint={t('airtime_mile_rate_help', { defaultValue: 'Falls back to the wallet redeem rate when empty. Card mile-price = card value ÷ rate, rounded up.' })}
              changed={isChanged('mileAirtimeRate')}
              disabled={!airtimeOn}
              control={
                <Box>
                  {numberField('mileAirtimeRate', { disabled: !airtimeOn })}
                  {effectiveAirtimeRate > 0 ? (
                    <Typography variant="body2" sx={{ mt: 0.75, color: c.TEXT_3, maxWidth: 280 }}>
                      {t('airtime_rate_example', {
                        symbol,
                        miles: Math.ceil((50 / effectiveAirtimeRate) * 100) / 100,
                        defaultValue: 'Example: a 50 {{symbol}} card costs {{miles}} miles at this rate.',
                      })}
                    </Typography>
                  ) : null}
                </Box>
              }
            />
            <SettingRow
              id="miles-airtimeDenominations"
              label={t('airtime_denominations', { defaultValue: 'Card denominations (ETB, comma separated)' })}
              hint={t('airtime_denominations_help', { defaultValue: 'Defaults to 50,100,200 when empty. Must match the card values you import on the Phone Cards page.' })}
              changed={isChanged('airtimeDenominations')}
              disabled={!airtimeOn}
              control={
                <TextField
                  id="miles-airtimeDenominations"
                  size="small"
                  value={data.airtimeDenominations}
                  onChange={(e) => setData((d) => ({ ...d, airtimeDenominations: e.target.value }))}
                  disabled={!airtimeOn}
                  placeholder="50,100,200"
                  inputProps={{ inputMode: 'numeric', 'aria-describedby': 'miles-airtimeDenominations-hint' }}
                  sx={{ width: { xs: '100%', sm: 280 } }}
                />
              }
            />
            <SettingRow
              id="miles-providers"
              layout="stack"
              label={t('airtime_providers_label', { defaultValue: 'Providers' })}
              hint={t('airtime_providers_help', {
                defaultValue:
                  "Upload each operator's logo (square works best) so customers instantly recognize their provider. Disabling a provider hides it in the app and blocks redemptions server-side; its card stock stays untouched.",
              })}
              changed={isChanged('airtimeProviderEnabled') || isChanged('airtimeProviderIcons') || Object.keys(iconFiles).some((k) => iconFiles[k])}
              disabled={!airtimeOn}
              control={
                <Box sx={{ border: `1px solid ${c.OUTLINE}`, borderRadius: '10px' }}>
                  {PROVIDERS.map((p, idx) => {
                    const logo = iconFiles[p.key] ? URL.createObjectURL(iconFiles[p.key]) : data.airtimeProviderIcons?.[p.key];
                    const on = data.airtimeProviderEnabled?.[p.key] !== false;
                    const switchId = `miles-provider-${p.key}`;
                    return (
                      <Box
                        key={p.key}
                        sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, px: 1.5, py: 1.25, borderTop: idx ? `1px solid ${c.OUTLINE}` : 0, opacity: airtimeOn ? 1 : 0.6 }}
                      >
                        {logo ? (
                          <Box component="img" alt={p.label} src={logo} sx={{ width: 44, height: 44, borderRadius: '10px', objectFit: 'contain', border: `1px solid ${c.OUTLINE}`, bgcolor: c.WHITE, flex: 'none' }} />
                        ) : (
                          <Box sx={{ width: 44, height: 44, borderRadius: '10px', border: `1px dashed ${c.OUTLINE_STRONG}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.TEXT_3, fontSize: 11, textAlign: 'center', flex: 'none' }}>
                            {t('no_logo', { defaultValue: 'no logo' })}
                          </Box>
                        )}
                        <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
                          <Typography component="label" htmlFor={switchId} sx={{ fontSize: 15, fontWeight: 600, color: c.INK, display: 'block' }}>
                            {p.label}
                          </Typography>
                          <Button component="label" size="small" variant="text" startIcon={<UploadRoundedIcon />} disabled={!airtimeOn} sx={{ px: 0.75, ml: -0.75 }}>
                            {iconFiles[p.key] ? iconFiles[p.key].name : t('upload_logo', { defaultValue: 'Upload logo' })}
                            <input hidden type="file" accept="image/*" onChange={(e) => setIconFiles({ ...iconFiles, [p.key]: e.target.files[0] })} />
                          </Button>
                        </Box>
                        <Switch
                          id={switchId}
                          checked={on}
                          onChange={(e) =>
                            setData((d) => ({
                              ...d,
                              airtimeProviderEnabled: { ...d.airtimeProviderEnabled, [p.key]: e.target.checked },
                            }))
                          }
                          disabled={!airtimeOn}
                        />
                      </Box>
                    );
                  })}
                </Box>
              }
            />
          </Box>

          <Box sx={GROUP_SX}>
            {/* Donkey Meter raffle */}
            <GroupTitle pairFirst icon={<EmojiEventsOutlinedIcon />} hue="amber" title={t('meter_reward_section', { defaultValue: 'Donkey Meter raffle' })} />
            <SettingRow
              id="miles-meterRewardEnabled"
              layout="switch"
              label={t('meter_reward_sub', { defaultValue: 'Every Nth confirmed-paid meter trip platform-wide wins a free airtime card' })}
              hint={t('meter_reward_help_short', {
                defaultValue:
                  "Counts trips across ALL drivers the moment each driver confirms the cash payment. The card comes from the Phone Cards inventory, matched to the winner's SIM, and is delivered by SMS. Out of stock? The win rolls to the next trip after a restock — never lost.",
              })}
              changed={isChanged('meterRewardEnabled')}
              control={<Switch id="miles-meterRewardEnabled" checked={meterOn} onChange={(e) => setSwitch('meterRewardEnabled', e.target.checked)} sx={{ my: -0.75 }} />}
            />
            <SettingRow
              id="miles-meterRewardEveryTrips"
              label={t('meter_reward_every_short', { defaultValue: 'Winner every N trips' })}
              changed={isChanged('meterRewardEveryTrips')}
              disabled={!meterOn}
              control={numberField('meterRewardEveryTrips', { disabled: !meterOn, placeholder: '10', min: 1, step: 1 })}
            />
            <SettingRow
              id="miles-meterRewardCardValue"
              label={t('meter_reward_value', { defaultValue: 'Card value (ETB)' })}
              changed={isChanged('meterRewardCardValue')}
              disabled={!meterOn}
              control={numberField('meterRewardCardValue', { disabled: !meterOn, placeholder: '50', min: 1, step: 1 })}
            />
            <SettingRow
              id="miles-meterRewardIncludeGuests"
              layout="switch"
              label={t('meter_reward_include_guests', { defaultValue: 'Also include street-pickup customers without an app account' })}
              changed={isChanged('meterRewardIncludeGuests')}
              disabled={!meterOn}
              control={
                <Switch
                  id="miles-meterRewardIncludeGuests"
                  checked={data.meterRewardIncludeGuests === true}
                  onChange={(e) => setSwitch('meterRewardIncludeGuests', e.target.checked)}
                  disabled={!meterOn}
                  sx={{ my: -0.75 }}
                />
              }
            />

            {/* NGO donations */}
            <GroupTitle icon={<VolunteerActivismOutlinedIcon />} hue="danger" title={t('mile_donate_section', { defaultValue: 'NGO donations' })} />
            <SettingRow
              id="miles-mileDonateEnabled"
              layout="switch"
              label={t('mile_donate_sub', { defaultValue: 'Customers donate their miles to partner organizations' })}
              hint={`${t('mile_donate_help', { defaultValue: 'Customers donate miles to the NGOs managed on the NGOs page. Reports value donations at the wallet redeem rate.' })} ${t('mile_donate_help2', { defaultValue: 'Track results on the Mile Donations page (admin & finance).' })}`}
              changed={isChanged('mileDonateEnabled')}
              control={<Switch id="miles-mileDonateEnabled" checked={data.mileDonateEnabled === true} onChange={(e) => setSwitch('mileDonateEnabled', e.target.checked)} sx={{ my: -0.75 }} />}
            />
          </Box>
        </Box>
        <Box sx={{ pb: 1 }} />
        <SaveBar
          changedCount={changed.length}
          onDiscard={discard}
          onSave={handleSubmit}
          saving={saving}
          sx={{ mx: { xs: -2, md: -2.5 }, px: { xs: 2, md: 2.5 } }}
        />
      </SectionCard>
    </Box>
  );
};

export default MileSettings;
